import axiosAPI from '../../utils/client/axiosAPI';
import { toFormData } from '../../utils/helpers/helpers';
import { ResponseWithPagination } from '../contracts';
import { StorageRoom } from '../Storage/StorageRoom';
import { StorageStand } from '../Storage/StorageStand';
import {
  ProductAttribute,
  ProductFeature,
  ProductResponse,
  ProductStatus,
  ProductType,
  StoreProductPayload
} from './contracts';
import { MediaResponse } from '../Media/contracts';
import { Media } from '../Media/Media';

/**
 * Product class representing product entities and their interactions with API endpoints.
 */
export class Product {
  // Class properties
  public readonly id: number;
  public readonly type: ProductType;
  public readonly name: string;
  public readonly sku: string;
  public readonly price: number;
  public readonly penaltyPrice?: number;
  public readonly shortDescription?: string;
  public readonly points: number;
  public readonly category: any;
  public readonly variations?: Product[];
  public readonly attributes?: ProductAttribute[];
  public readonly features?: ProductFeature[];
  public readonly storageRoom?: StorageRoom;
  public readonly storageStand?: StorageStand;
  public readonly coverImage?: MediaResponse | null;
  public readonly gallery?: MediaResponse[];
  public readonly status: ProductStatus;

  /**
   * Constructor to initialize a Product instance from ProductResponse data.
   * @param data ProductResponse object.
   */
  constructor(data: ProductResponse) {
    this.id = data.id;
    this.type = data.type;
    this.name = data.name;
    this.sku = data.sku;
    this.price = data.price;
    this.penaltyPrice = data.penalty_price;
    this.shortDescription = data.short_description;
    this.points = data.points;
    this.category = data.category;
    this.variations = data.variations?.map((v) => new Product(v));

    this.coverImage = data.cover_image ?? null;
    this.gallery = data.gallery ?? [];
    this.storageRoom = data.storage_room_stand?.storage_room
      ? new StorageRoom(data.storage_room_stand.storage_room)
      : undefined;
    this.storageStand = data.storage_room_stand
      ? new StorageStand(data.storage_room_stand)
      : undefined;
    this.attributes = data.attribute_values ?? [];
    this.features = data.feature_values ?? [];
    this.status = data.status;
  }

  // ---------- Static Methods for CRUD operations ----------

  /** Fetch a single product by ID. */
  public static async get(id: string | number): Promise<Product> {
    const response = await axiosAPI.get<ProductResponse>(`/products/${id}`);
    return new Product(response.data);
  }

  /** Create a new product with provided payload. */
  public static async store(payload: StoreProductPayload): Promise<Product> {
    const response = await axiosAPI.post<ProductResponse>(
      'admin/products',
      toFormData(payload)
    );
    return new Product(response.data);
  }

  /** Retrieve a paginated list of products. */
  public static async all(
    params: any
  ): Promise<ResponseWithPagination<Product>> {
    const endpoint = params.isAdmin ? '/admin/products' : '/products';
    const { data: response } = await axiosAPI.get<
      ResponseWithPagination<ProductResponse>
    >(endpoint, { params });
    return {
      ...response,
      data: response.data.map((product) => new Product(product))
    };
  }

  /** Fetch products specifically for order processing. */
  public static async listForOrder(): Promise<Product[]> {
    const response = await axiosAPI.get<ProductResponse[]>(
      '/admin/orders/all-products'
    );
    return response.data.map((product) => new Product(product));
  }

  /** Reset product price to match its category default. */
  static async resetPriceToCategory(productId: number): Promise<Product> {
    const response = await axiosAPI.post<ProductResponse>(
      `admin/products/${productId}/reset-price-to-category`
    );
    return new Product(response.data);
  }

  /** Delete a product by ID. */
  public static async delete(id: number): Promise<void> {
    await axiosAPI.delete<null>(`admin/products/${id}`);
  }

  /** Delete a specific image from a product. */
  public static async deleteImage(
    productId: number,
    imageId: number
  ): Promise<void> {
    await axiosAPI.delete(`/admin/products/${productId}/images/${imageId}`);
  }

  // ---------- Instance Methods ----------

  /**
   * Update the product instance with new data.
   * @param payload Data to update the product.
   */
  public async update(payload: StoreProductPayload): Promise<Product> {
    if (!this.id) {
      throw new Error('Product ID is missing.');
    }

    const formData = toFormData(payload);
    formData.append('_method', 'PUT');

    const response = await axiosAPI.post<ProductResponse>(
      `admin/products/${this.id}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return new Product(response.data);
  }

  /** Delete this specific product instance. */
  public async delete(): Promise<void> {
    await axiosAPI.delete<null>(`admin/products/${this.id}`);
  }

  /**
   * Retrieve the main image URL for the product.
   * Prefer cover image; fallback to first gallery image.
   */
  public getMainImageUrl(): string | null {
    if (this.coverImage) {
      const media =
        this.coverImage instanceof Media
          ? this.coverImage
          : new Media(this.coverImage);
      return media.fullUrl;
    }

    if (this.gallery?.length) {
      const galleryImg =
        this.gallery[0] instanceof Media
          ? this.gallery[0]
          : new Media(this.gallery[0]);
      return galleryImg.fullUrl;
    }

    return null;
  }
}
